<template>
    <div :id="'car-' + carPackage.fingerprint" class="list-group-item p-0 border-secondary">
        <div class="row no-gutters">
            <div class="col-3">
                <div class="d-flex flex-column justify-content-between h-100">
                    <div class="pt-2 pl-2 pr-2">
                        <h5 class="mb-0 text-truncate">{{ vehicle.modelName }}</h5>
                        <small v-if="vehicle.isModelOrSimilar" class="text-sm text-muted model-or-similar">
                            {{ $t('cars.search.results.orSimilar') }}
                        </small>
                    </div>
                    <div class="text-center pl-2 pr-2">
                        <img :src="vehicle.pictureUri" :alt="vehicle.modelName">
                        <div class="btn btn-xs bg-secondary-light d-block disabled">{{ vehicle.className }}</div>
                    </div>
                    <table class="table table-sm text-sm table-bordered mb-0 border-left-0 border-right-0 border-bottom-0 text-center pt-2">
                        <tr>
                            <td v-b-tooltip.hover
                                :title="$t('cars.search.results.passengerTooltip')"
                                class="border-left-0 border-bottom-0"
                                style="width: 33.33%">
                                {{ vehicle.passengerQuantity }} <i class="fa fa-fw fa-user"></i>
                            </td>
                            <td v-b-tooltip.hover
                                :title="$t('cars.search.results.baggageTooltip')"
                                class="border-bottom-0"
                                style="width: 33.33%">
                                {{ vehicle.baggageQuantity }} <i class="fa fa-fw fa-suitcase"></i>
                            </td>
                            <td v-b-tooltip.hover
                                :title="$t('cars.search.results.doorTooltip')"
                                class="border-right-0 border-bottom-0"
                                style="width: 33.34%">
                                {{ vehicle.doorCount }} <i class="fa fa-fw fa-window-maximize"></i>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
            <div class="col-6">
                <table class="table table-bordered table-sm text-sm mb-0 border-top-0 border-bottom-0">
                    <tr>
                        <th class="border-top-0" style="width: 32%">
                            {{ $t('cars.search.results.pickupLocation') }}
                        </th>
                        <td class="border-top-0">
                            {{ carPackage.pickupLocation.counterLocationString }}
                            <i v-b-tooltip.hover
                               :title="carPackage.pickupLocation.address"
                               class="fa fa-map-marker"></i>
                        </td>
                    </tr>
                    <tr>
                        <th style="width: 32%">
                            {{ $t('cars.search.results.returnLocation') }}
                        </th>
                        <td>
                            {{ carPackage.returnLocation.counterLocationString }}
                            <i v-b-tooltip.hover
                               :title="carPackage.returnLocation.address"
                               class="fa fa-map-marker"></i>
                        </td>
                    </tr>
                    <tr>
                        <th style="width: 32%">
                            {{ $t('cars.search.results.mileageAllowance') }}
                        </th>
                        <td>{{ carPackage.mileageAllowanceString }}</td>
                    </tr>
                    <tr>
                        <th style="width: 32%">
                            {{ $t('cars.search.results.fuelPolicy') }}
                        </th>
                        <td>{{ carPackage.fuelPolicyString }}</td>
                    </tr>
                    <tr>
                        <th style="width: 32%">
                            {{ $t('cars.search.results.fuelType') }}
                        </th>
                        <td>{{ vehicle.fuelTypeString }}</td>
                    </tr>
                    <tr>
                        <th style="width: 32%">
                            {{ $t('cars.search.results.transmissionType') }}
                        </th>
                        <td>{{ vehicle.transmissionTypeString }}</td>
                    </tr>
                    <tr>
                        <th style="width: 32%">
                            {{ $t('cars.search.results.driveType') }}
                        </th>
                        <td>{{ vehicle.driveTypeString }}</td>
                    </tr>
                    <tr>
                        <th class="td-shrink border-bottom-0">
                            {{ $t('cars.search.results.airConditioning') }}
                        </th>
                        <td class="border-bottom-0" v-if="vehicle.isAirConditioned">
                            {{ $t('cars.search.results.yes') }}
                        </td>
                        <td class="border-bottom-0" v-else>
                            {{ $t('cars.search.results.no') }}
                        </td>
                    </tr>
                </table>
            </div>
            <div class="col-3">
                <div class="d-flex flex-column justify-content-center bg-secondary-xlight h-100 p-2 text-center">
                    <div>
                        <img :src="vendor.largePictureUri" :alt="vendor.shortName">
                    </div>
                    <div class="m-3">
                        <h4 class="mb-0">{{ carPackage.totalPrice | formatMoney }}</h4>
                        <div v-if="rentalDuration > 1">
                            ({{ averageDailyPrice | formatMoney }} {{ $t('cars.search.results.perDay') }})
                        </div>
                        <div class="text-sm">{{ carPackage.rateQualifierString }}</div>
                    </div>
                    <div>
                        <a v-if="isInternal"
                           class="btn btn-secondary btn-sm text-white"
                           @click.prevent="debugPackage()">
                            <i class="fa fa-bug"></i>
                        </a>
                        <a href="#" class="btn btn-info btn-sm" @click.prevent="$emit('quote', carPackage)">
                            <i class="fa fa-save fa-fw"></i> {{ $t('cars.search.results.quote') }}
                        </a>
                        <a :href="carPackage.checkoutPageUri" class="btn btn-primary btn-sm" target="_blank">
                            <i class="fa fa-car fa-fw"></i> {{ $t('cars.search.results.book') }}
                        </a>
                    </div>
                    <div class="mt-1">
                        <a href="#" @click.prevent="showConditions" class="text-sm text-secondary">
                            {{ $t('cars.search.results.terms') }}
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <b-modal ref="packageConditions"
                 :title="$t('cars.search.results.terms')"
                 :cancel-title="$t('cars.search.results.cancel')"
                 :ok-disabled="true"
                 size="lg">
            <dl>
                <template v-for="(condition, title) in carPackage.conditions">
                    <dt>{{ title }}</dt>
                    <dd v-html="condition.replace(/\n/g, '<br>')"></dd>
                </template>
            </dl>
        </b-modal>

    </div>
</template>

<script>
    export default {
        props: ['carPackage'],
        computed: {
            vehicle() {
                return this.carPackage.vehicle;
            },
            vendor() {
                return this.carPackage.vendor;
            },
            averageDailyPrice() {
                return this.carPackage.totalPrice / this.rentalDuration;
            },
            rentalDuration() {
                let startOfDay = {
                    hour: 0,
                    minute: 0,
                    second: 0
                };
                let pickupDate = moment(this.carPackage.pickupDate).set(startOfDay);
                let returnDate = moment(this.carPackage.returnDate).set(startOfDay);
                return Math.max(1, moment.duration(returnDate.diff(pickupDate)).asDays());
            },
            vendorLocationMapsUri() {
                return 'https://www.google.com/maps/search/?api=1'
                    + '&query=' + encodeURIComponent(this.vendor.shortName + ' ' + this.carPackage.pickupLocation.address);
            },
            hasConditions() {
                if (Array.isArray(this.carPackage.conditions) && this.carPackage.conditions.length === 0) {
                    return false;
                }

                return Object.keys(this.carPackage.conditions).length > 0;
            }
        },
        methods: {
            debugPackage() {
                let url = `${window.env.baseUri}/cars/package-details/${this.carPackage.searchId}/${this.carPackage.packageId}`;
                axios.get(url).then((response) => {
                    var modal = $('#debug-modal');
                    var table = modal.find('.table tbody');
                    modal.find('.debug-package').remove();

                    var content = response.data.success ? response.data.data : 'Package Not Found';
                    $('<tr class="table-secondary debug-package">' +
                        '<th class="td-shrink">Package</th>' +
                        '<td class="p-0">' + content + '</td><td></td>' +
                        '</tr>')
                        .appendTo(table);
                    modal.modal('show');
                });
            },
            loadConditions() {
                let url = `${window.env.baseUri}/cars/package-conditions/${this.carPackage.searchId}/${this.carPackage.packageId}`;
                axios.get(url).then((response) => {
                    if (response.data.success) {
                        this.carPackage.conditions = response.data.conditions;
                        this.showConditions();
                    }
                });
            },
            showConditions() {
                if (!this.hasConditions) {
                    this.loadConditions();
                    return;
                }

                this.$refs.packageConditions.show();
            },
        }
    };
</script>

<style scoped>

</style>